/**
 * One colour per metric, the same on every surface.
 *
 * Operator request (2026-09-02): "colour code the metrics so that we can relate
 * to them elsewhere like in the monthly view". The Jour row, the Semaine grid
 * and the Mois cell all render the same handful of figures in different shapes;
 * before this, only their position told you which was which, and the Mois cell
 * has no position — it is `« 22 ch. · 4 cvts »`, a run of numbers whose labels
 * are gone. Colour is the one channel that survives that compression.
 *
 * ── Why a lookup and not `assignEmployeeColors`'s sorted-index scheme ─────
 *
 * Employee colours are assigned by sorted id, which is stable only while the
 * SET is stable — add an employee and everyone after them shifts a hue. That is
 * fine for chips you read against a legend on the same screen. It is wrong
 * here: the whole point is that « Départs » is the same colour in a month cell
 * today and in a day row next April, across a metric registry the operator adds
 * rows to. So the mapping is by KEY, and a key nobody has named falls back to a
 * hash of the key — stable for that key forever, and never disturbing another.
 *
 * Hues are the `EMPLOYEE_COLORS` family (WCAG-friendly on white, and already
 * proven on this board) chosen for meaning where meaning exists: arrivals and
 * departures are opposites and read as such; the two restaurant figures sit
 * together; the staffing load is the darkest thing on the row, because it is
 * the number the shift is actually built from.
 */

/** Colour by metric key. Profile outputs match on their key SUFFIX, since the
 *  generated key carries the profile's id (`profile3ABlancEquivalents`). */
const BY_KEY: Record<string, string> = {
  arrivals: '#2f7d3b', // arriving — green
  departures: '#a5341f', // leaving, and the work it makes — red
  occupancy: '#1f6b86', // the night's rooms — the board's anchor figure
  stayovers: '#3f7f7f', // occupancy minus arrivals — a cousin of it, teal
  breakfasts: '#a55a14', // morning — amber
  packedLunches: '#8a6b1f', // midday, between breakfast's amber and covers' olive
  halfBoards: '#6b3a9c', // evening, contracted — purple
  dinersHotel: '#8f5ab8', // evening, same table, not contracted — a lighter purple
  diners: '#b3487d', // the one typed figure — deliberately distinct
  covers: '#7a6210', // halfBoards + dinersHotel + diners
  // The people dimension (0129), shown inside the Occupation cell.
  guestsAdults: '#1f6b86',
  guestsChildren: '#1f6b86',
  arrivalsGuests: '#2f7d3b',
  departuresGuests: '#a5341f',
};

const BY_SUFFIX: [string, string][] = [
  ['ABlancEquivalents', '#2a3957'], // « Charge <profil> » — the staffing load
  ['WorkMinutes', '#5a5a8a'],
  ['Heads', '#556b2f'],
];

/** Deterministic fallback for a metric the operator added. */
const FALLBACK = ['#8a7a55', '#5a5a8a', '#556b2f', '#3f7f7f', '#7a6210', '#b3487d'] as const;

export function metricColor(key: string): string {
  const exact = BY_KEY[key];
  if (exact) return exact;
  for (const [suffix, color] of BY_SUFFIX) if (key.endsWith(suffix)) return color;
  // djb2, taken positive. Any stable hash would do; what matters is that the
  // same key gets the same hue on every surface and every deploy.
  let h = 5381;
  for (let i = 0; i < key.length; i++) h = ((h << 5) + h + key.charCodeAt(i)) | 0;
  return FALLBACK[Math.abs(h) % FALLBACK.length] as string;
}
